import {
	getEventTypeDisplayName,
	getHoldingsOptionsForSelect,
	hasAssignmentEvent,
	hasPledgeEvent,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import {
	summarizeOrFormatCost,
	summarizeOrFormatUnits,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/holdings.utils.ts';
import { RegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { numberSearch } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/utils/numberSearch';
import { HoldingsType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsController.tsx';
import { TableCellWithEvents } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/TableCellWithEvents.tsx';
import { NumericCell } from '@dealstack/frontend/shared/components/Table/MantineTable/Cells/NumericCell';
import { createHoldingActionColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/ButtonColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { createNumericHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { mapBlocksToText } from '@dealstack/shared/lib/format/block.ts';
import { numberToUiPercent } from '@dealstack/shared/lib/format/number';
import {
	HoldingEventRes,
	HoldingEventType,
	HoldingSecurityRes,
	SecurityIssuedIn,
} from '@dealstack/shared/lib/generated';
import { Badge, Group, MantineTheme, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

type RegularViewTableColumn = ExtendedColumnDef<RegularViewTableItemData>;

interface Props {
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	showSummarized: boolean;
	showPercentage: boolean;
	securitiesNames?: HoldingSecurityRes[];
	tableType?: HoldingsType;
	onCreateEvent?: (holding: RegularViewTableItemData) => void;
	onEditEvent?: (holding: RegularViewTableItemData, events: HoldingEventRes[]) => void;
	onDeleteEvent?: (holding: RegularViewTableItemData, events: HoldingEventRes[]) => void;
	viewByBlock?: boolean;
	theme?: MantineTheme;
}

export const getRegularViewTableColumns = ({
	tCapManage,
	showSummarized,
	showPercentage,
	securitiesNames,
	tableType,
	onCreateEvent,
	onEditEvent,
	onDeleteEvent,
	viewByBlock,
}: Props): RegularViewTableColumn[] => {
	return [
		createTextColumn({
			id: 'holderName',
			headerText:
				tableType === HoldingsType.OwnershipTable
					? tCapManage('holdings.table.columns.holder')
					: tCapManage('holdings.table.columns.owner'),
			filterText:
				tableType === HoldingsType.OwnershipTable
					? tCapManage('holdings.table.filter.holder')
					: tCapManage('holdings.table.filter.owner'),
			columnLabel:
				tableType === HoldingsType.OwnershipTable
					? tCapManage('holdings.table.columns.holder')
					: tCapManage('holdings.table.columns.owner'),
			showFilter: true,
			accessorFn: (row: RegularViewTableItemData) =>
				tableType === HoldingsType.OwnershipTable ? row.holderName : row.securityPersonName,
			sortingFn: (rowA, rowB) => {
				const valA =
					tableType === HoldingsType.OwnershipTable
						? rowA.original.holderName
						: rowA.original.securityPersonName;
				const valB =
					tableType === HoldingsType.OwnershipTable
						? rowB.original.holderName
						: rowB.original.securityPersonName;

				const normalizedA = (valA ?? '').toLowerCase();
				const normalizedB = (valB ?? '').toLowerCase();

				return normalizedA.localeCompare(normalizedB);
			},
		}),
		createTextColumn({
			id: 'mipName',
			headerText: tCapManage('holdings.table.columns.holding'),
			filterText: tCapManage('holdings.table.filter.holding'),
			columnLabel: tCapManage('holdings.table.columns.holding'),
			showFilter: true,
			accessorFn: (row: RegularViewTableItemData) => row.mipName ?? 'No Mip',
			sortingFn: (rowA, rowB) => {
				const nameA = rowA.original.mipName?.toLowerCase() || '';
				const nameB = rowB.original.mipName?.toLowerCase() || '';
				return nameA.localeCompare(nameB);
			},
		}),
		createTextColumn({
			id: 'heldFor',
			headerText: tCapManage('holdings.table.columns.held_for'),
			filterText: tCapManage('holdings.table.filter.held_for'),
			columnLabel: tCapManage('holdings.table.columns.held_for'),
			showFilter: true,
			accessorFn: (row: RegularViewTableItemData) => row.heldForName ?? '',
			sortingFn: (rowA, rowB) => {
				const nameA = rowA.original.heldForName?.toLowerCase() || '';
				const nameB = rowB.original.heldForName?.toLowerCase() || '';
				return nameA.localeCompare(nameB);
			},
			cell: ({ cell }) => {
				const value = cell.getValue<string>();
				return <Text size="B6Medium">{value}</Text>;
			},
		}),
		createTextColumn({
			id: 'securityName',
			headerText: tCapManage('transactions.table.columns.security'),
			filterText: tCapManage('transactions.table.filter.security'),
			columnLabel: tCapManage('transactions.table.columns.security'),
			showFilter: true,
			accessorFn: (row: RegularViewTableItemData) => row.securityName,
			sortingFn: (rowA, rowB) => {
				return rowA.original.securityName
					.toLowerCase()
					.localeCompare(rowB.original.securityName.toLowerCase());
			},
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: getHoldingsOptionsForSelect(securitiesNames),
			},
		}),
		createTextColumn({
			id: 'unitsAmount',
			header: createNumericHeader(tCapManage('holdings.table.columns.unit_amount')),
			filterText: tCapManage('holdings.table.filter.unit_amount'),
			filterFn: (row, columnId, filterValue) => {
				const value: { unitsAmount: number; unitsPercentage: number } =
					row.getValue(columnId);

				return numberSearch(
					showPercentage ? value?.unitsPercentage : value?.unitsAmount,
					filterValue
				);
			},
			columnLabel: tCapManage('holdings.table.columns.unit_amount'),
			showFilter: true,
			accessorFn: (row: RegularViewTableItemData) => ({
				unitsAmount: row.unitsAmount ?? 0,
				unitsPercentage: row.unitsPercentage,
				securityUnitDenomination: row.securityUnitDenomination,
				securityIssuedIn: row.securityIssuedIn,
				securityCurrency: row.securityCurrency,
				totalCost: row.totalCost ?? 0,
				events: row.events ?? [],
			}),
			sortingFn: (rowA, rowB) =>
				(rowA.original.unitsAmount ?? 0) - (rowB.original.unitsAmount ?? 0),
			cell: ({ cell }) => {
				const data = cell.getValue<{
					unitsAmount: number;
					unitsPercentage: number;
					securityUnitDenomination: number;
					securityIssuedIn: SecurityIssuedIn;
					securityCurrency?: string;
					totalCost: number;
					events: HoldingEventRes[];
				}>();

				let value: string;

				if (showPercentage) {
					value = numberToUiPercent(data.unitsPercentage) ?? LONG_DASH;
				} else if (
					data.securityIssuedIn === SecurityIssuedIn.CURRENCY &&
					data.securityCurrency
				) {
					value = `${data.securityCurrency} ${summarizeOrFormatCost(data.totalCost, showSummarized)}`;
				} else {
					value = summarizeOrFormatUnits(
						data.unitsAmount,
						data.securityUnitDenomination,
						showSummarized
					);
				}

				const hasAssignment = hasAssignmentEvent(data.events);
				const hasPledge = hasPledgeEvent(data.events);

				return (
					<TableCellWithEvents
						hasAssignment={hasAssignment}
						hasPledge={hasPledge}
						value={value}
						copyable={false}
						numeric
					/>
				);
			},
		}),
		...(viewByBlock
			? []
			: [
					createTextColumn({
						id: 'totalCost',
						header: createNumericHeader(tCapManage('holdings.table.columns.cost')),
						filterText: tCapManage('holdings.table.filter.cost'),
						columnLabel: tCapManage('holdings.table.columns.cost'),
						showFilter: true,
						cell: ({ cell }) => (
							<NumericCell
								value={summarizeOrFormatCost(
									cell.getValue<number>(),
									showSummarized
								)}
								c={showPercentage ? 'gray.4' : 'dark.4'}
							/>
						),
						filterVariant: 'text',
						accessorFn: (row: RegularViewTableItemData) => row.totalCost ?? 0,
						filterFn: (row, columnId, filterValue) => {
							const value: number = row.getValue(columnId);
							return numberSearch(value, filterValue);
						},
						sortingFn: (rowA, rowB) =>
							(rowA.original.totalCost ?? 0) - (rowB.original.totalCost ?? 0),
					}),
					createTextColumn({
						id: 'votesAmount',
						header: createNumericHeader(
							tCapManage('holdings.table.columns.votes_amount')
						),
						filterText: tCapManage('holdings.table.filter.votes_amount'),
						columnLabel: tCapManage('holdings.table.columns.votes_amount'),
						showFilter: true,
						filterVariant: 'text',
						accessorFn: (row: RegularViewTableItemData) =>
							showPercentage
								? numberToUiPercent(row.votesPercentage) || LONG_DASH
								: summarizeOrFormatUnits(
										row.votesAmount ?? 0,
										row.securityUnitDenomination,
										showSummarized
									),
						cell: ({ cell }) => <NumericCell value={cell.getValue<string>()} />,
						filterFn: (row, columnId, filterValue) => {
							const rawValue: number = row.getValue(columnId);

							return numberSearch(rawValue, filterValue);
						},
						sortingFn: (rowA, rowB) =>
							(rowA.original.votesAmount ?? 0) - (rowB.original.votesAmount ?? 0),
					}),
				]),
		createTextColumn({
			id: 'associatedBlocks',
			headerText: tCapManage('holdings.table.columns.associated_blocks'),
			filterText: tCapManage('holdings.table.filter.associated_blocks'),
			columnLabel: tCapManage('holdings.table.columns.associated_blocks'),
			showFilter: true,
			cellTextProps: {
				truncate: true,
				lineClamp: 2,
			},
			accessorFn: (row: RegularViewTableItemData) =>
				mapBlocksToText(row.associatedBlocks, row.securityUnitDenomination),
		}),
		createTextColumn({
			id: 'totalNominalValue',
			header: createNumericHeader(tCapManage('holdings.table.columns.total_nominal_value')),
			columnLabel: tCapManage('holdings.table.columns.total_nominal_value'),
			accessorFn: (row: RegularViewTableItemData) => row.totalNominalValue,
			filterFn: (row, columnId, filterValue) => {
				const rawValue: number = row.getValue(columnId);

				return numberSearch(rawValue, filterValue);
			},
			cell: ({ cell }) =>
				renderMoneyCell(cell.getValue<number>(), showPercentage, showSummarized),
			sortingFn: (rowA, rowB) =>
				(rowA.original.totalNominalValue ?? 0) - (rowB.original.totalNominalValue ?? 0),
		}),
		createTextColumn({
			id: 'totalSharePremium',
			header: createNumericHeader(tCapManage('holdings.table.columns.total_share_premium')),
			columnLabel: tCapManage('holdings.table.columns.total_share_premium'),
			accessorFn: (row: RegularViewTableItemData) => row.totalSharePremium,
			filterFn: (row, columnId, filterValue) => {
				const rawValue: number = row.getValue(columnId);

				return numberSearch(rawValue, filterValue);
			},
			cell: ({ cell }) =>
				renderMoneyCell(cell.getValue<number>(), showPercentage, showSummarized),
			sortingFn: (rowA, rowB) =>
				(rowA.original.totalSharePremium ?? 0) - (rowB.original.totalSharePremium ?? 0),
		}),
		createTextColumn({
			id: 'totalShareCapital',
			header: createNumericHeader(tCapManage('holdings.table.columns.total_share_capital')),
			columnLabel: tCapManage('holdings.table.columns.total_share_capital'),
			accessorFn: (row: RegularViewTableItemData) => row.totalShareCapital,
			filterFn: (row, columnId, filterValue) => {
				const rawValue: number = row.getValue(columnId);

				return numberSearch(rawValue, filterValue);
			},
			cell: ({ cell }) =>
				renderMoneyCell(cell.getValue<number>(), showPercentage, showSummarized),
			sortingFn: (rowA, rowB) =>
				(rowA.original.totalShareCapital ?? 0) - (rowB.original.totalShareCapital ?? 0),
		}),
		createTextColumn({
			id: 'events',
			headerText: tCapManage('holdings.event.events'),
			filterText: tCapManage('holdings.table.filter.events'),
			columnLabel: tCapManage('holdings.table.columns.events'),
			showFilter: true,
			accessorFn: (row: RegularViewTableItemData) => {
				return row.events;
			},
			filterFn: (row, columnId, filterValue) => {
				const rawValue: RegularViewTableItemData['events'] = row.getValue(columnId);
				const searchString =
					rawValue
						?.map((event) => getEventTypeDisplayName(event.type, tCapManage))
						.join(' ')
						.toLowerCase() || '';

				return searchString.includes(String(filterValue).toLowerCase());
			},
			sortingFn: (rowA, rowB) =>
				(rowA.original.events?.length ?? 0) - (rowB.original.events?.length ?? 0),
			cell: ({ row }) => {
				const events = row.original.events || [];
				const eventsCount = events.length;

				if (eventsCount === 0) {
					return <Text size="B6Medium">{LONG_DASH}</Text>;
				}

				const firstEvent = events[0];
				const firstEventType = firstEvent.type as HoldingEventType;
				const displayName = getEventTypeDisplayName(firstEventType, tCapManage);

				return (
					<Group gap="xs" wrap="nowrap">
						<Text size="B6Medium">{displayName}</Text>
						{eventsCount > 1 && (
							<Badge
								size="sm"
								variant="filled"
								color="gray"
								radius="xl"
								sx={{
									backgroundColor: defaultTheme.v3.colors.gray[4],
									color: defaultTheme.v3.colors.dark[4],
									fontWeight: 'normal',
									fontSize: '12px',
								}}
							>
								+{eventsCount - 1}
							</Badge>
						)}
					</Group>
				);
			},
		}),
		createHoldingActionColumn(tCapManage, onCreateEvent, onEditEvent, onDeleteEvent),
	];
};

const renderMoneyCell = (value: number, showPercentage: boolean, showSummarized: boolean) => (
	<NumericCell
		value={
			showPercentage
				? numberToUiPercent(value) || LONG_DASH
				: summarizeOrFormatCost(value, showSummarized)
		}
		c={showPercentage ? 'gray.4' : 'dark.4'}
	/>
);
